import S from 's-js';
import { IAjax } from 'lib/Ajax';
import { TZUtils, IClock } from 'lib/tzutils';
import { tzmomentMod } from 'lib/tzmoment';

import { Entrypoints } from 'core/app/entrypoints';

import { EnhancedEstimateQueryStatus } from 'server/enums/EnhancedEstimateQuery';

import { AutomationSystem } from 'data/models/AutomationSystem';
import { AutomationSystemType } from 'data/models/AutomationSystemType';
import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { EnhancedEstimate } from 'data/models/EnhancedEstimate';
import { EnhancedEstimateQuery } from 'data/models/EnhancedEstimateQuery';
import { WartsilaEnrichmentsResponse } from 'data/models/WartsilaEnrichmentsResponse';

import { PCCache } from 'data/PCCache';

export interface WartsilaEnrichmentsEstimateDetailsParams {
    model : WartsilaEnrichmentsResponse;
    // A promise that indicates we're ready to try an initial refresh if this component
    // chooses to do one. This is essentially a way to avoid races reading/writing Issues.
    // Note that it doesn't matter whether this promise resolves or rejects, just when it completes.
    readyForInitialRefresh : PromiseLike<any>;
}

export type WartsilaEnrichmentsEstimateDetails = ReturnType<typeof WartsilaEnrichmentsEstimateDetails>;
export function WartsilaEnrichmentsEstimateDetails(
    data : PCCache,
    ajax : IAjax,
    entrypoints : Entrypoints,
    tzutils : TZUtils,
    { model, readyForInitialRefresh } : WartsilaEnrichmentsEstimateDetailsParams
) {
    const
        // Clock for displaying time ago for time of last known vessel location.
        // Once a minute is plenty fast.
        clock = tzutils.clock(60 * 1000),
        automation = S(() => {
            const automations = Array.from(data.AutomationSystems.items()).filter(sys =>
                sys.Type() === AutomationSystemType.WartsilaEnhancedEstimates &&
                sys.Active()
            );
            return automations.length ? automations[0] : null;
        }),
        initialRefreshPendingOrInProgress = S.value(true),
        estimateRefresher = S(() => automation() ?
            EstimateRefreshViewModel(
                model,
                automation()!,
                clock,
                ajax,
                entrypoints
            ) : null
        ),
        initialRefreshInitiated = S.value(false);

    S(async () => {
        if (!initialRefreshInitiated()) {
            const refresher = estimateRefresher();
            if (refresher) {
                initialRefreshInitiated(true);
                try {
                    await readyForInitialRefresh;
                } finally {
                    // We're ready for refresh. Now decide whether to actually do one.
                    // Do one provided --
                    //   1) We don't already have a RelevantQuery, and
                    //   2) This portcall is coming up w/in the next 48 hrs.
                    const
                        pc = model.PortCall(),
                        arrivalPt = pc && pc.Itinerary().find(pt => pt.IsPortArrival()) || null;
                    if (
                        !model.RelevantQuery() &&
                        arrivalPt &&
                        arrivalPt.Arrival.Actual() === null &&
                        arrivalPt.Arrival.Estimated() < tzmomentMod(tzutils, tzutils.now(), d => d.add(48, 'hours'))
                    ) {
                        try {
                            await refresher.RefreshEstimate();
                        } finally {
                            initialRefreshPendingOrInProgress(false);
                        }
                    } else {
                        initialRefreshPendingOrInProgress(false);
                    }
                }
            }
        }
    });

    return {
        RelevantQuery: S(() => model.RelevantQuery() === null ? null : EnhancedEstimateQueryViewModel(model.RelevantQuery()!, clock)),
        EstimateRefresher: estimateRefresher,
        InitialRefreshPendingOrInProgress: () => initialRefreshPendingOrInProgress(),
        AnyRefreshPendingOrInProgress: () => initialRefreshPendingOrInProgress() ||
            !!estimateRefresher() && estimateRefresher()!.Refreshing()
    };
}

export type EnhancedEstimateQueryViewModel = ReturnType<typeof EnhancedEstimateQueryViewModel>;
function EnhancedEstimateQueryViewModel(model : EnhancedEstimateQuery, clock : IClock) {
    return {
        Model: model,
        Result: S(() => model.Result() === null ? null : EnhancedEstimateViewModel(model.Result()!, clock))
    };
}

export type EnhancedEstimateViewModel = ReturnType<typeof EnhancedEstimateViewModel>;
function EnhancedEstimateViewModel(model : EnhancedEstimate, clock : IClock) {
    return {
        Model: model,
        // If we somehow didn't get a TimeOfKnownVesselLocation from the ETA endpoint,
        // go with the time the query was created, which should be accurate to within
        // an hour or so.
        Timestamp: () => model.TimeOfKnownVesselLocation() || model.Query() && model.Query()!.CreationDate(),
        Clock: clock
    };
}

export type EstimateRefreshViewModel = ReturnType<typeof EstimateRefreshViewModel>;
function EstimateRefreshViewModel(
    model : WartsilaEnrichmentsResponse,
    automation : AutomationSystem,
    clock : IClock,
    ajax : IAjax,
    entrypoints : Entrypoints,
) {
    const
        // NOTE -- Switching to S.data instead of S.value to avoid a known (to AH et al) bug
        // in S. See https://github.com/adamhaile/surplus/issues/65
        // AH has a fix pending, but in the meantime, S.data avoids the issue.
        // Also note that there are many other spots where we use this same pattern to
        // prevent overlapping requests from fast clicks.From a quick glance, looks like they
        // all use S.data already.
        refreshing = S.data(false),
        failedRefreshAttempt = S.value<EnhancedEstimateQuery | null>(null),
        failedRefreshAttemptVM = S(() =>
            failedRefreshAttempt() ? {
                query: EnhancedEstimateQueryViewModel(failedRefreshAttempt()!, clock),
                dismiss: () => { failedRefreshAttempt(null); }
             } : null
        ),
        url = entrypoints.WartsilaRefreshEstimate({ PCId: model.Id(), AutomationId: automation.Id() }),
        client = ajax.Url<{}, DataCacheUpdateEntities>(url, {
            useCache: true
        });

    return {
        FailedRefreshAttempt: failedRefreshAttemptVM,
        RefreshEstimate,
        Refreshing: () => refreshing()
    };

    async function RefreshEstimate() {
        if (refreshing()) return;
        refreshing(true);
        try {
            const
                req = client.Post({}),
                wartsilaUpdates = (await req).WartsilaEntities();
            S.freeze(() => {
                const
                    oldQuery = model.RelevantQuery(),
                    newQuery = wartsilaUpdates && wartsilaUpdates.EnhancedEstimateQueries().length ?
                        wartsilaUpdates.EnhancedEstimateQueries()[0] : null;
                if (!newQuery) throw new Error("Expected the DataCacheUpdate to include a new EnhancedEstimateQuery");
                if (isMoreRelevant(newQuery, oldQuery)) {
                    model.RelevantQuery(newQuery);
                    failedRefreshAttempt(null); // Clear any previous failure message
                } else {
                    failedRefreshAttempt(newQuery);
                }
            });
        } finally {
            refreshing(false);
        }
    }
}

function isMoreRelevant(newQuery : EnhancedEstimateQuery, oldQuery : EnhancedEstimateQuery | null) {
    // New query is more relevant than the old one provided
    //   - There was no old query, OR
    //   - The old query was unsuccessful. Any more recent query is more relevant in this case, OR
    //   - The new query is successful.
    return oldQuery === null ||
        oldQuery.Status() !== EnhancedEstimateQueryStatus.Succeeded ||
        newQuery.Status() === EnhancedEstimateQueryStatus.Succeeded;
}



// WEBPACK FOOTER //
// ./apps/wartsila/wartsilaenrichments/estimateDetails.ts